import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, RouterOutlet } from "@angular/router";
import { distinctUntilChanged, filter, map, Subscription, take } from "rxjs";
import { ToastComponent } from "./components/toast/toast.component";
import {
  KioskLocalStorageKeysEnum,
  LocalStorageService,
} from "./services/local-storage.service";
import { WakeLockService } from "./services/wake-lock.service";

@Component({
  selector: "db-kiosk-root",
  standalone: true,
  imports: [RouterOutlet, ToastComponent],
  template: `
    <router-outlet></router-outlet>
    <db-toast></db-toast>
  `,
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly localStorageService = inject(LocalStorageService);
  private readonly route = inject(ActivatedRoute);
  private readonly wakeLockService = inject(WakeLockService);

  private subscription: Subscription | null = null;

  platform$ = this.route.queryParams.pipe(
    map((params) => params["platform"] as string),
    filter(Boolean),
    distinctUntilChanged(),
  );

  ngOnInit(): void {
    this.setPlatform();
    this.setLocale();

    // Wait a short time for the app to be fully initialized
    setTimeout(() => {
      // Enable wake lock for the entire application
      void this.wakeLockService.enableWakeLock().then((success) => {
        if (success) {
          console.log(
            `Wake lock enabled using: ${this.wakeLockService.getActiveStrategy()}`,
          );
        } else {
          console.warn(
            "Initial wake lock request failed, service will retry automatically",
          );
        }
      });
    }, 300);

    // Optionally monitor status changes
    this.subscription = this.wakeLockService.activeStrategy$.subscribe(
      (strategy) => {
        console.log(`Active wake lock strategy changed to: ${strategy}`);
      },
    );
  }

  setPlatform(): void {
    this.platform$.pipe(take(1)).subscribe((platform) => {
      if (platform) {
        this.localStorageService.setItem(
          KioskLocalStorageKeysEnum.PLATFORM,
          platform,
        );
      }
    });
  }

  setLocale(): void {
    this.localStorageService.setItem("locale", "en");
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.wakeLockService.disableWakeLock();
  }
}
